<template>
  <button
    ref="root"
    type="button"
    class="roster-overlay"
    :class="{ compact: duelCompact }"
    @click="skip"
  >
    <div class="letterbox top" />
    <div class="letterbox bot" />
    <div class="vignette" />
    <div class="scan" />

    <p class="kicker">{{ t('duel.roster.kicker') }}</p>

    <div class="stage">
      <div
        v-for="side in sides"
        :key="side.id"
        class="card"
        :class="side.id"
      >
        <AvatarView :config="side.avatar" :size="avatarSize" />
        <div class="copy">
          <p class="side-tag" :class="{ show: step >= 2 }">{{ side.tag }}</p>
          <h2 class="name" :class="{ show: step >= 2 }">{{ side.name }}</h2>

          <div class="rank" :class="{ show: step >= 3 }">
            <RankBadge
              v-if="side.badge"
              :league="side.badge.league"
              :division="side.badge.division"
              :size="badgeSize"
            />
            <div class="rank-copy">
              <p class="rank-name">{{ side.rankTitle }}</p>
              <p v-if="side.lpLabel" class="lp">{{ side.lpLabel }}</p>
            </div>
          </div>

          <p class="level" :class="{ show: step >= 4 }">{{ side.levelLabel }}</p>

          <div v-if="side.chips.length" class="chips">
            <span
              v-for="(chip, index) in side.chips"
              :key="chip.key"
              class="chip"
              :class="{ show: step >= 5 + index }"
            >
              {{ chip.label }}
            </span>
          </div>

          <div class="rate" :class="{ show: step >= rateAt }">
            <p
              v-if="side.streak"
              class="streak"
              :class="side.streak.kind"
            >
              {{ side.streak.label }}
            </p>
            <template v-if="side.winPct !== null">
              <p class="pct">{{ side.winPct }}%</p>
              <div class="bar" aria-hidden="true">
                <span
                  class="fill"
                  :style="{ width: step >= rateAt ? side.winPct + '%' : '0%' }"
                />
              </div>
              <p class="record">{{ side.recordLabel }}</p>
            </template>
            <p v-else class="quiet">{{ side.emptyRecord }}</p>
          </div>
        </div>
      </div>
    </div>

    <p class="skip-hint">{{ t('duel.vs.skip') }}</p>
  </button>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import {
  ROSTER_INTRO_MS,
  isPlacement,
  type AvatarConfig,
  type DuelRosterCard,
  type LeagueDivision,
  type LeagueId,
} from '@shared/index'
import { useI18n } from '../i18n/t'
import { useCompactLayout } from '../composables/useCompactLayout'
import { DuelSfx } from '../game/audio/DuelSfx'
import AvatarView from './AvatarView.vue'
import RankBadge from './RankBadge.vue'

const props = defineProps<{
  youName: string
  oppName: string
  youAvatar: AvatarConfig
  oppAvatar: AvatarConfig
  you: DuelRosterCard
  opp: DuelRosterCard
}>()

const emit = defineEmits<{
  done: []
}>()

const NAME_AT = 240
const RANK_AT = 560
const LEVEL_AT = 880
const PLACES_AT = 1160
const PLACE_STEP = 170
const AFTER_PLACES = 220
const REDUCED_MS = 900

const { t } = useI18n()
const { duelCompact } = useCompactLayout()
const root = ref<HTMLButtonElement | null>(null)
const step = ref(0)
const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

const avatarSize = computed(() => (duelCompact.value ? 64 : 200))
const badgeSize = computed(() => (duelCompact.value ? 36 : 72))

interface Chip {
  key: string
  label: string
}

interface SideView {
  id: 'you' | 'opp'
  tag: string
  name: string
  avatar: AvatarConfig
  card: DuelRosterCard
  badge: { league: LeagueId; division: LeagueDivision | null } | null
  rankTitle: string
  lpLabel: string
  levelLabel: string
  chips: Chip[]
  winPct: number | null
  recordLabel: string
  emptyRecord: string
  streak: { kind: 'win' | 'loss'; label: string } | null
}

function chipsFor(card: DuelRosterCard): Chip[] {
  const places = card.places
  const rows: Chip[] = []
  if (places.ranked != null) {
    rows.push({
      key: 'ranked',
      label: t('duel.roster.placeRanked', { n: places.ranked }),
    })
  }
  if (places.level != null) {
    rows.push({
      key: 'level',
      label: t('duel.roster.placeLevel', { n: places.level }),
    })
  }
  for (const id of ['hard', 'expert', 'master'] as const) {
    const place = places.ai[id]
    if (place == null) continue
    rows.push({
      key: id,
      label: t('duel.roster.placeAi', {
        name: t(`settings.vsAiLevels.${id}`),
        n: place,
      }),
    })
  }
  return rows
}

function rankTitle(card: DuelRosterCard): string {
  const standing = card.standing
  if (!standing) return t('duel.roster.noRank')
  if (isPlacement(standing) || !standing.league) {
    return standing.placementRemaining > 0
      ? t('duel.roster.placementLeft', { n: standing.placementRemaining })
      : t('duel.roster.placement')
  }
  const label = t(`rank.leagues.${standing.league}`)
  if (!standing.division || standing.league === 'king') return label
  return `${label} ${standing.division === 1 ? 'I' : 'II'}`
}

function lpLabel(card: DuelRosterCard): string {
  const standing = card.standing
  if (!standing?.league || isPlacement(standing)) return ''
  if (standing.league === 'king') return t('rank.lpBare', { lp: standing.lp })
  return t('rank.lpCount', { lp: standing.lp })
}

function recordLabel(card: DuelRosterCard): string {
  const { wins, losses, draws } = card.record
  if (draws > 0) return t('duel.roster.recordDraws', { wins, losses, draws })
  return t('duel.roster.record', { wins, losses })
}

function emptyRecord(card: DuelRosterCard): string {
  const { wins, losses, draws } = card.record
  if (wins + losses + draws === 0) return t('duel.roster.noDuels')
  return recordLabel(card)
}

function streakView(
  card: DuelRosterCard,
): { kind: 'win' | 'loss'; label: string } | null {
  const streak = card.record.streak
  if (!streak || streak.count < 2) return null
  if (streak.kind !== 'win' && streak.kind !== 'loss') return null
  const key = streak.kind === 'win' ? 'duel.roster.winStreak' : 'duel.roster.lossStreak'
  return { kind: streak.kind, label: t(key, { n: streak.count }) }
}

function present(
  id: 'you' | 'opp',
  tag: string,
  name: string,
  avatar: AvatarConfig,
  card: DuelRosterCard,
): SideView {
  const standing = card.standing
  const badge =
    standing?.league && !isPlacement(standing)
      ? { league: standing.league, division: standing.division }
      : null
  const decided = card.record.wins + card.record.losses
  return {
    id,
    tag,
    name,
    avatar,
    card,
    badge,
    rankTitle: rankTitle(card),
    lpLabel: lpLabel(card),
    levelLabel: t('duel.roster.level', { n: card.level }),
    chips: chipsFor(card),
    winPct: decided > 0 ? Math.round((card.record.wins / decided) * 100) : null,
    recordLabel: recordLabel(card),
    emptyRecord: emptyRecord(card),
    streak: streakView(card),
  }
}

const sides = computed(() => [
  present('you', t('duel.roster.you'), props.youName, props.youAvatar, props.you),
  present('opp', t('duel.roster.rival'), props.oppName, props.oppAvatar, props.opp),
])

const maxChips = computed(() =>
  Math.max(sides.value[0]?.chips.length ?? 0, sides.value[1]?.chips.length ?? 0),
)
const rateAt = computed(() => 5 + maxChips.value)

let finished = false
let timers: ReturnType<typeof setTimeout>[] = []

function clearTimers(): void {
  for (const id of timers) clearTimeout(id)
  timers = []
}

function later(ms: number, fn: () => void): void {
  timers.push(setTimeout(fn, ms))
}

function finish(): void {
  if (finished) return
  finished = true
  clearTimers()
  emit('done')
}

function skip(): void {
  finish()
}

onMounted(() => {
  root.value?.focus()
  DuelSfx.play('packWhoosh', 0.55)
  const count = maxChips.value
  if (reduced) {
    step.value = 100
    timers.push(setTimeout(finish, REDUCED_MS))
    return
  }
  step.value = 1
  later(NAME_AT, () => { step.value = 2 })
  later(RANK_AT, () => { step.value = 3 })
  later(LEVEL_AT, () => { step.value = 4 })
  for (let i = 0; i < count; i++) {
    const at = PLACES_AT + i * PLACE_STEP
    const value = 5 + i
    later(at, () => { step.value = value })
  }
  const winAt = count === 0 ? LEVEL_AT + 280 : PLACES_AT + count * PLACE_STEP + AFTER_PLACES
  later(winAt, () => { step.value = 5 + count })
  timers.push(setTimeout(finish, ROSTER_INTRO_MS))
})

onUnmounted(() => {
  clearTimers()
})
</script>

<style scoped>
.roster-overlay {
  position: fixed;
  inset: 0;
  z-index: 520;
  margin: 0;
  padding: 0;
  border: 0;
  overflow: hidden;
  cursor: pointer;
  color: #f4f0ea;
  font: inherit;
  text-align: left;
  background:
    radial-gradient(ellipse at 16% 48%, rgba(20, 80, 90, 0.5), transparent 52%),
    radial-gradient(ellipse at 84% 48%, rgba(120, 24, 32, 0.5), transparent 52%),
    linear-gradient(180deg, #05060c 0%, #101018 48%, #05060c 100%);
}

.letterbox {
  position: absolute;
  left: 0;
  right: 0;
  height: 5vh;
  z-index: 2;
  pointer-events: none;
  background: #000;
}

.letterbox.top {
  top: 0;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.45);
}

.letterbox.bot {
  bottom: 0;
  box-shadow: 0 -18px 40px rgba(0, 0, 0, 0.45);
}

.vignette {
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  background: radial-gradient(
    ellipse at 50% 48%,
    transparent 42%,
    rgba(0, 0, 0, 0.55) 100%
  );
}

.scan {
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  opacity: 0.16;
  background-image: repeating-linear-gradient(
    180deg,
    transparent,
    transparent 2px,
    rgba(0, 0, 0, 0.35) 3px
  );
}

.kicker {
  position: absolute;
  top: calc(5vh + 18px);
  left: 0;
  right: 0;
  z-index: 4;
  margin: 0;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.42em;
  text-transform: uppercase;
  color: #fde68a;
  text-shadow: 0 0 18px rgba(232, 197, 71, 0.55);
  animation: kicker-in 0.55s ease-out both;
}

.stage {
  position: absolute;
  z-index: 4;
  left: 4%;
  right: 4%;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  pointer-events: none;
}

.card {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  max-width: 520px;
  padding: 0 12px;
}

.card.you {
  animation: card-left 0.55s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.card.opp {
  flex-direction: row-reverse;
  text-align: right;
  margin-left: auto;
  animation: card-right 0.55s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.copy {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.card.opp .copy {
  align-items: flex-end;
}

.side-tag,
.name,
.rank,
.level,
.chip,
.rate {
  opacity: 0;
}

.side-tag.show,
.name.show,
.rank.show,
.level.show,
.chip.show,
.rate.show {
  animation: rise 0.42s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.side-tag {
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.28em;
  text-transform: uppercase;
}

.you .side-tag {
  color: #5eead4;
}

.opp .side-tag {
  color: #fca5a5;
}

.name {
  margin: 0;
  max-width: 14ch;
  font-size: clamp(22px, 3vw, 40px);
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  line-height: 1.05;
}

.you .name {
  color: #99f6e4;
  text-shadow:
    0 0 18px rgba(45, 212, 191, 0.55),
    0 3px 14px rgba(0, 0, 0, 0.85);
}

.opp .name {
  color: #fca5a5;
  text-shadow:
    0 0 18px rgba(248, 113, 113, 0.55),
    0 3px 14px rgba(0, 0, 0, 0.85);
}

.rank {
  display: flex;
  align-items: center;
  gap: 10px;
}

.card.opp .rank {
  flex-direction: row-reverse;
}

.rank-copy {
  min-width: 0;
}

.rank-name,
.lp,
.level,
.record,
.quiet {
  margin: 0;
}

.rank-name {
  font-size: clamp(14px, 1.6vw, 18px);
  font-weight: 700;
  letter-spacing: 0.04em;
}

.lp,
.level {
  color: #e7e5e4;
  font-size: 14px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.level {
  font-size: clamp(16px, 1.8vw, 22px);
  font-weight: 700;
  color: #fde68a;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-width: 280px;
}

.card.opp .chips {
  justify-content: flex-end;
}

.chip {
  padding: 3px 8px;
  border-radius: 999px;
  border: 1px solid rgba(232, 197, 71, 0.55);
  background: rgba(8, 8, 14, 0.72);
  color: #fde68a;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.streak {
  display: inline-block;
  width: fit-content;
  margin: 0 0 6px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.streak.win {
  border: 1px solid rgba(74, 222, 128, 0.75);
  background: rgba(22, 101, 52, 0.88);
  color: #bbf7d0;
}

.streak.loss {
  border: 1px solid rgba(248, 113, 113, 0.75);
  background: rgba(127, 29, 29, 0.88);
  color: #fecaca;
}

.pct {
  margin: 0;
  font-size: clamp(26px, 3vw, 42px);
  font-weight: 800;
  line-height: 1;
  letter-spacing: 0.02em;
}

.you .pct {
  color: #99f6e4;
}

.opp .pct {
  color: #fca5a5;
}

.bar {
  width: min(180px, 28vw);
  height: 6px;
  margin-top: 6px;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.12);
}

.card.opp .bar {
  margin-left: auto;
}

.fill {
  display: block;
  height: 100%;
  width: 0;
  border-radius: inherit;
  transition: width 0.7s cubic-bezier(0.22, 1, 0.36, 1);
}

.you .fill {
  background: linear-gradient(90deg, #0f766e, #5eead4);
}

.opp .fill {
  background: linear-gradient(90deg, #9f1239, #fca5a5);
}

.record,
.quiet {
  margin-top: 4px;
  color: #d6d3d1;
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.quiet {
  font-size: 15px;
  font-weight: 700;
  color: #fde68a;
}

.skip-hint {
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(5vh + 8px);
  z-index: 4;
  margin: 0;
  text-align: center;
  font-size: 11px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #c6c5d0;
  opacity: 0.75;
}

.compact .kicker {
  top: calc(5vh + 4px);
  letter-spacing: 0.16em;
  font-size: 11px;
}

.compact .stage {
  left: max(10px, env(safe-area-inset-left));
  right: max(10px, env(safe-area-inset-right));
  top: calc(5vh + 26px);
  bottom: calc(5vh + 20px);
  transform: none;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.compact .card,
.compact .card.opp {
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 4px;
  max-width: none;
  margin: 0;
  padding: 0 8px;
}

.compact .copy,
.compact .card.opp .copy {
  align-items: center;
  width: 100%;
  gap: 3px;
}

.compact .name {
  max-width: 16ch;
  letter-spacing: 0.02em;
  font-size: clamp(13px, 2.6vw, 18px);
  line-height: 1.05;
  overflow-wrap: anywhere;
}

.compact .rank,
.compact .card.opp .rank {
  flex-direction: row;
  justify-content: center;
  gap: 6px;
}

.compact .rank-name,
.compact .lp {
  font-size: 12px;
}

.compact .level {
  font-size: 13px;
}

.compact .chips,
.compact .card.opp .chips {
  justify-content: center;
  max-width: 100%;
  gap: 4px;
}

.compact .chip {
  padding: 2px 6px;
  font-size: 10px;
}

.compact .streak {
  margin-bottom: 4px;
  padding: 2px 6px;
  font-size: 10px;
}

.compact .pct {
  font-size: clamp(18px, 3vw, 26px);
}

.compact .bar,
.compact .card.opp .bar {
  width: min(120px, 26vw);
  margin: 3px auto 0;
}

.compact .record,
.compact .quiet {
  font-size: 10px;
  letter-spacing: 0.06em;
}

.compact .skip-hint {
  bottom: calc(5vh + 2px);
  letter-spacing: 0.12em;
}

@media (max-height: 380px) and (max-width: 1100px) {
  .compact .stage {
    top: calc(5vh + 20px);
    bottom: calc(5vh + 14px);
    gap: 8px;
  }

  .compact :deep(.avatar-view) {
    width: 52px !important;
    height: 52px !important;
  }
}

@keyframes kicker-in {
  from {
    opacity: 0;
    letter-spacing: 0.12em;
  }
  to {
    opacity: 1;
  }
}

@keyframes card-left {
  from {
    opacity: 0;
    transform: translateX(-36px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@keyframes card-right {
  from {
    opacity: 0;
    transform: translateX(36px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@keyframes rise {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .kicker,
  .card.you,
  .card.opp,
  .side-tag.show,
  .name.show,
  .rank.show,
  .level.show,
  .chip.show,
  .rate.show {
    animation: none;
    opacity: 1;
    transform: none;
  }

  .fill {
    transition: none;
  }
}
</style>
